import i18n from '@/utils/i18n';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  buildSalsaUrl,
  MicroProductWebViewRef,
  NavigateToMicroproductPayload,
  RouteChangedPayload,
} from '@domain/microproducts/components/micro-product-webview-types';
import { isWeb } from '@/utils/navigation';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { MicroProductsSlot } from '@domain/microproducts/loaders/use-microproducts-loader';
import { MicroProductWebView } from '@domain/microproducts/components/micro-product-webview';
import { NavigationProp, ParamListBase } from '@react-navigation/native';
import {
  partnerSupplier,
  partnerSupplierAppURL,
  salsaConfig,
} from '@/utils/environment';
import { Platform, StyleSheet, View } from 'react-native';
import { SalesPortalNotFoundScreen } from '@/experiences/sales-portal/screens/sales-portal-not-found-screen';
import { SessionService } from '@domain/auth/blocs/services/session-service';
import { Spinner } from '@usepepper/sprout-kit-ui';
import { SUPPORTED_HOST_ROUTES } from '@/experiences/sales-portal/sales-portal-route-config';
import { useBottomNavScrollHandler } from '@sales-portal/blocs/hooks/use-bottom-nav-scroll-handler';
import { useExperienceUIStore } from '@experiences/blocs/stores/experience-ui-store';
import { useMicroProductHostNavigation } from '@/experiences/sales-portal/domain/microproducts/blocs/hooks/use-micro-product-host-navigation';
import { useMicroProductScreenLoader } from '@domain/microproducts/loaders/use-microproduct-screen-loader';
import { useSidebarToggleHandler } from '@experiences/templates/use-sidebar-toggle-handler';

const requestAuthToken = () => SessionService.getIdToken();

interface MicroProductScreenViewProps {
  microproductParams?: Record<string, string>;
  /** Opaque in-product route: seeds the SPA on load and is mirrored back into
   *  the host URL as the user drills in (deep-link / refresh support). */
  mpRoute?: string;
  navigation: {
    dispatch: NavigationProp<ParamListBase>['dispatch'];
    setOptions: (options: { title?: string }) => void;
    setParams: (params: { mpRoute?: string }) => void;
  };
  /** Strategy for SPA-initiated microproduct→microproduct navigation; owns the
   *  target route and host params (e.g. pricing preserves supplierUUID). */
  onNavigateToMicroproduct: (payload: NavigateToMicroproductPayload) => void;
  productId?: string;
  /** Restrict product resolution to this slot. Scoped surfaces (e.g. pricing)
   *  pass their slot so a product only renders if configured for them. */
  slot?: MicroProductsSlot;
  /** Shell rendered around the iframe (host sidebar + chrome). */
  Template: React.ComponentType<{ children: ReactNode }>;
}

/**
 * Shared body for sales-portal microproduct screens: loads the product, builds
 * the salsa URL, and wires the webview. Callers inject the `Template` shell and
 * the microproduct-navigation strategy — the only host-specific pieces.
 */
export const MicroProductScreenView: React.FC<MicroProductScreenViewProps> = ({
  Template,
  microproductParams,
  mpRoute,
  navigation,
  onNavigateToMicroproduct,
  productId,
  slot,
}) => {
  const { product, loading } = useMicroProductScreenLoader({ productId, slot });
  const webViewRef = useRef<MicroProductWebViewRef>(null);

  // Seed the iframe with a deep-linked route at load time only. Captured per
  // product load: productId is the reload boundary, so when it changes we
  // re-read the current mpRoute. Live ROUTE_CHANGED updates call setParams to
  // refresh the address bar but MUST NOT feed back into `uri` — otherwise every
  // drill would rebuild the src and reload the iframe.
  const seedRouteRef = useRef<{ hash?: string; productId?: string }>({});
  if (seedRouteRef.current.productId !== productId) {
    seedRouteRef.current = { hash: mpRoute, productId };
  }

  // Route SPA-initiated sidebar toggles (hamburger in microproduct's own
  // PageLayout.leadingActions) to the right store action based on viewport
  const handleSidebarToggle = useSidebarToggleHandler();

  const { layoutSize } = useLayoutContext();
  const isSidebarCollapsed = useExperienceUIStore(
    (state) => state.isSidebarCollapsed,
  );
  const isDrawerLayout =
    layoutSize === LayoutSize.SMALL || layoutSize === LayoutSize.SMALL_TABLET;
  // Memoize to keep prop identity stable across re-renders; the webview's
  // SIDEBAR_STATE push effect depends on this object and should only fire
  // when the underlying primitives actually change.
  const sidebarState = useMemo(
    () => ({ isCollapsed: isSidebarCollapsed, isDrawerLayout }),
    [isSidebarCollapsed, isDrawerLayout],
  );

  const handleNavigateToHost = useMicroProductHostNavigation(
    navigation,
    webViewRef,
  );

  const isMobileBottomNavLayout = !isWeb && layoutSize === LayoutSize.SMALL;
  const handleContentScroll = useBottomNavScrollHandler();

  const handleRouteChanged = useCallback(
    ({ route }: RouteChangedPayload) => {
      // Mirror the opaque in-product route into the host URL (shareable +
      // survives refresh). setParams updates the address bar in place — the
      // iframe's own pushState already drives the browser back stack. Keep it
      // bare (strip leading '#'); clear the param for root routes.
      const bare = route.replace(/^#/, '');
      const isRoot = bare === '' || bare === '/';
      navigation.setParams({ mpRoute: isRoot ? undefined : bare });
    },
    [navigation],
  );

  useEffect(() => {
    const title = loading
      ? i18n.t('sales_portal.home.title')
      : product?.displayName || i18n.t('sales_portal.not_found.title');
    navigation.setOptions({ title });
  }, [loading, navigation, product?.displayName]);

  if (loading) {
    return (
      <Template>
        <View style={styles.loadingContainer}>
          <Spinner size="large" />
        </View>
      </Template>
    );
  }

  if (!product) {
    return (
      <Template>
        <SalesPortalNotFoundScreen />
      </Template>
    );
  }

  const uri = buildSalsaUrl({
    businessOrganizationId: partnerSupplier.businessOrganizationUUID,
    extraParams: { ...product.props, ...(microproductParams ?? {}) },
    forwardedHost: partnerSupplierAppURL,
    locale: i18n.locale,
    platform: Platform.OS as 'web' | 'ios' | 'android',
    productId: product.id,
    routeHash: seedRouteRef.current.hash,
    salsaBaseUrl: salsaConfig.webUrl,
  });

  // Microproducts own their chrome — render flush (no host top bar).
  // Product title + mobile hamburger live in the SPA's own PageLayout.
  return (
    <Template>
      <MicroProductWebView
        onAuthTokenRequest={requestAuthToken}
        onBodyScroll={isMobileBottomNavLayout ? handleContentScroll : undefined}
        onNavigateToHost={handleNavigateToHost}
        onNavigateToMicroproduct={onNavigateToMicroproduct}
        onRouteChanged={handleRouteChanged}
        onSidebarToggle={handleSidebarToggle}
        ref={webViewRef}
        sidebarState={sidebarState}
        supportedHostRoutes={SUPPORTED_HOST_ROUTES}
        uri={uri}
      />
    </Template>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
});
